/** Toplantı gündem zaman çizelgesi — saf hesaplamalar */

export const AGENDA_WARN_SECONDS = 10;

export interface AgendaTopicInput {
  id?: string;
  title: string;
  /** Dakika */
  duration: number;
  type?: string;
  order?: number;
}

export interface AgendaSegment {
  index: number;
  id: string;
  title: string;
  type: string;
  durationSec: number;
  startOffsetSec: number;
  endOffsetSec: number;
}

/** Socket / oda senkronu */
export interface AgendaTimelineSync {
  meetingId?: string;
  startedAt: number;
  /** Host atlattıktan sonra bu indeksin altına düşülmez */
  currentIndex: number;
  /** Son atlatma anı; null = saf startedAt zaman çizelgesi */
  advancedAt: number | null;
}

export interface AgendaResolvedState {
  index: number;
  segment: AgendaSegment | null;
  nextSegment: AgendaSegment | null;
  remainingSec: number;
  elapsedInItemSec: number;
  warnSoon: boolean;
  isComplete: boolean;
}

export function buildAgendaSegments(topics: AgendaTopicInput[]): AgendaSegment[] {
  const sorted = [...topics].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  let offset = 0;
  return sorted.map((topic, index) => {
    const durationSec = Math.max(0, Math.round((topic.duration || 0) * 60));
    const startOffsetSec = offset;
    const endOffsetSec = offset + durationSec;
    offset = endOffsetSec;
    return {
      index,
      id: topic.id || `topic-${index}`,
      title: topic.title || `Madde ${index + 1}`,
      type: topic.type || 'bilgilendirme',
      durationSec,
      startOffsetSec,
      endOffsetSec,
    };
  });
}

function resolveFromElapsed(
  elapsedSec: number,
  segments: AgendaSegment[],
  minIndex: number
): AgendaResolvedState {
  if (segments.length === 0) {
    return {
      index: 0,
      segment: null,
      nextSegment: null,
      remainingSec: 0,
      elapsedInItemSec: 0,
      warnSoon: false,
      isComplete: true,
    };
  }

  const clampedMin = Math.max(0, Math.min(minIndex, segments.length - 1));

  if (elapsedSec >= segments[segments.length - 1].endOffsetSec) {
    const last = segments[segments.length - 1];
    return {
      index: last.index,
      segment: last,
      nextSegment: null,
      remainingSec: 0,
      elapsedInItemSec: last.durationSec,
      warnSoon: false,
      isComplete: true,
    };
  }

  const timeIndex = (() => {
    for (let i = 0; i < segments.length; i += 1) {
      if (elapsedSec < segments[i].endOffsetSec) return i;
    }
    return segments.length - 1;
  })();

  const index = Math.max(timeIndex, clampedMin);
  const segment = segments[index];
  const nextSegment = index + 1 < segments.length ? segments[index + 1] : null;

  let elapsedInItemSec: number;
  let remainingSec: number;

  if (index > timeIndex) {
    elapsedInItemSec = 0;
    remainingSec = segment.durationSec;
  } else {
    elapsedInItemSec = Math.max(0, elapsedSec - segment.startOffsetSec);
    remainingSec = Math.max(0, segment.endOffsetSec - elapsedSec);
  }

  return {
    index,
    segment,
    nextSegment,
    remainingSec,
    elapsedInItemSec,
    warnSoon: remainingSec > 0 && remainingSec <= AGENDA_WARN_SECONDS,
    isComplete: false,
  };
}

/**
 * Host atlatmasından sonra: currentIndex maddesi advancedAt'ten tam süresiyle başlar;
 * sonraki maddeler peş peşe devam eder.
 */
function resolveFromAdvance(
  elapsedSinceAdvanceSec: number,
  segments: AgendaSegment[],
  currentIndex: number
): AgendaResolvedState {
  if (segments.length === 0) {
    return {
      index: 0,
      segment: null,
      nextSegment: null,
      remainingSec: 0,
      elapsedInItemSec: 0,
      warnSoon: false,
      isComplete: true,
    };
  }

  const start = Math.max(0, Math.min(currentIndex, segments.length - 1));
  let offset = 0;

  for (let i = start; i < segments.length; i += 1) {
    const seg = segments[i];
    const end = offset + seg.durationSec;
    if (elapsedSinceAdvanceSec < end) {
      const elapsedInItemSec = Math.max(0, elapsedSinceAdvanceSec - offset);
      const remainingSec = Math.max(0, end - elapsedSinceAdvanceSec);
      const nextSegment = i + 1 < segments.length ? segments[i + 1] : null;
      return {
        index: i,
        segment: seg,
        nextSegment,
        remainingSec,
        elapsedInItemSec,
        warnSoon: remainingSec > 0 && remainingSec <= AGENDA_WARN_SECONDS,
        isComplete: false,
      };
    }
    offset = end;
  }

  const last = segments[segments.length - 1];
  return {
    index: last.index,
    segment: last,
    nextSegment: null,
    remainingSec: 0,
    elapsedInItemSec: last.durationSec,
    warnSoon: false,
    isComplete: true,
  };
}

export function resolveAgendaState(
  sync: AgendaTimelineSync,
  segments: AgendaSegment[],
  now = Date.now()
): AgendaResolvedState {
  if (segments.length === 0) {
    return {
      index: 0,
      segment: null,
      nextSegment: null,
      remainingSec: 0,
      elapsedInItemSec: 0,
      warnSoon: false,
      isComplete: true,
    };
  }

  if (sync.advancedAt != null) {
    const elapsedSinceAdvance = Math.max(
      0,
      Math.floor((now - sync.advancedAt) / 1000)
    );
    return resolveFromAdvance(elapsedSinceAdvance, segments, sync.currentIndex);
  }

  const elapsedSec = Math.max(0, Math.floor((now - sync.startedAt) / 1000));
  return resolveFromElapsed(elapsedSec, segments, sync.currentIndex);
}

/** Host bir sonraki maddeye geçer; yeni sync döner. Son maddedeyse null. */
export function advanceAgendaSync(
  sync: AgendaTimelineSync,
  segments: AgendaSegment[],
  now = Date.now()
): AgendaTimelineSync | null {
  if (segments.length === 0) return null;
  const resolved = resolveAgendaState(sync, segments, now);
  if (resolved.isComplete || resolved.index >= segments.length - 1) {
    return null;
  }
  return {
    ...sync,
    currentIndex: resolved.index + 1,
    advancedAt: now,
  };
}

export function createInitialAgendaSync(startedAt = Date.now()): AgendaTimelineSync {
  return {
    startedAt,
    currentIndex: 0,
    advancedAt: null,
  };
}
